/**
 * Plumbing shared by every Integrations/Webhooks screen. Nothing here
 * renders — the screens own their markup; this file owns what they all
 * repeat.
 *
 * Status tones are kept local rather than added to `@/components/ui/badge`'s
 * `STATUS_TONES`: that table is `satisfies Record<DomainStatus, Tone>` over
 * the enums re-exported from `@/lib/shared-types`'s status-bearing set, and
 * several of the values below collide by *name* with an existing member
 * while meaning something different here — `FAILED` is a terminal job
 * outcome in `IntegrationJobStatus` and a still-retrying delivery in
 * `WebhookDeliveryStatus`/`IntegrationDeliveryStatus`, and `PENDING` is
 * warning-toned everywhere else in this app but is the ordinary, unremarkable
 * resting state of a delivery about to be attempted. `Pages/Fleet/fleetVisuals.tsx`
 * sets the precedent for keeping a feature's own status vocabulary local for
 * exactly this reason.
 */

export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand';

/**
 * Surface the API's own message (§3 envelope) rather than a generic failure.
 *
 * Handles two different error shapes on purpose. A direct `api.post/patch/delete`
 * call (every mutation in this feature) throws the raw axios error, so the
 * server's message lives at `error.response.data.message`. An RTK Query hook's
 * `error` (every list/detail read) has already been through `axiosBaseQuery`
 * (`Redux/baseApi.ts`), which extracts that same field once at the query
 * boundary and republishes it as a flat `{ status, message }` — there is no
 * `.response` left to read on that shape, only `.message`. Checking both is
 * what keeps a `PanelResult`'s "the query failed" state quoting the server
 * instead of silently falling back to the generic text for every list load.
 */
export function apiMessage(error: unknown, fallback: string): string {
  const payload = (error as { response?: { data?: { message?: string | string[] } } })?.response
    ?.data?.message;
  if (Array.isArray(payload)) return payload.filter(Boolean).join(' · ');
  if (typeof payload === 'string' && payload.trim()) return payload;
  const rtkMessage = (error as { message?: unknown } | null)?.message;
  if (typeof rtkMessage === 'string' && rtkMessage.trim()) return rtkMessage;
  return fallback;
}

/** The stable code on a coded refusal — `{ code, message, ... }` — when the API sent one. */
export function errorCode(error: unknown): string | null {
  const code = (error as { response?: { data?: { code?: unknown } } })?.response?.data?.code;
  return typeof code === 'string' ? code : null;
}

/** The HTTP status of a failed request, when there was a response at all. */
export function errorStatus(error: unknown): number | null {
  const status = (error as { response?: { status?: unknown } })?.response?.status;
  return typeof status === 'number' ? status : null;
}

export function formatDateTime(value: string | Date | null | undefined, locale: string): string {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

export function formatRelative(value: string | Date | null | undefined, locale: string): string {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const diffMs = date.getTime() - Date.now();
  const abs = Math.abs(diffMs);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (abs < hour) return rtf.format(Math.round(diffMs / minute), 'minute');
  if (abs < day) return rtf.format(Math.round(diffMs / hour), 'hour');
  return rtf.format(Math.round(diffMs / day), 'day');
}

const INSTALLATION_STATUS_TONE: Record<string, Tone> = {
  PENDING: 'warning',
  ACTIVE: 'success',
  SUSPENDED: 'neutral',
  UNINSTALLED: 'neutral',
};

const INSTALLATION_HEALTH_TONE: Record<string, Tone> = {
  UNKNOWN: 'neutral',
  HEALTHY: 'success',
  DEGRADED: 'warning',
  FAILING: 'danger',
};

const INTEGRATION_DELIVERY_TONE: Record<string, Tone> = {
  PENDING: 'neutral',
  DELIVERING: 'info',
  DELIVERED: 'success',
  FAILED: 'warning',
  DEAD_LETTERED: 'danger',
  CANCELLED: 'neutral',
};

const INTEGRATION_JOB_TONE: Record<string, Tone> = {
  QUEUED: 'neutral',
  RUNNING: 'info',
  SUCCEEDED: 'success',
  FAILED: 'danger',
  CANCELLED: 'neutral',
};

const WEBHOOK_ENDPOINT_TONE: Record<string, Tone> = {
  ACTIVE: 'success',
  DISABLED: 'neutral',
  DISABLED_FAILING: 'danger',
};

const WEBHOOK_DELIVERY_TONE: Record<string, Tone> = {
  PENDING: 'neutral',
  DELIVERING: 'info',
  SUCCEEDED: 'success',
  FAILED: 'warning',
  DEAD_LETTERED: 'danger',
  CANCELLED: 'neutral',
};

/** ADR-0003 — `ApiClient.status`. `REVOKED` is danger, not neutral: it is permanent, unlike a webhook's `DISABLED`. */
const API_CLIENT_STATUS_TONE: Record<string, Tone> = {
  ACTIVE: 'success',
  SUSPENDED: 'warning',
  REVOKED: 'danger',
};

function toneFrom(table: Record<string, Tone>, status?: string | null): Tone {
  if (!status) return 'neutral';
  return table[status] ?? 'neutral';
}

export const toneForInstallationStatus = (s?: string | null) => toneFrom(INSTALLATION_STATUS_TONE, s);
export const toneForInstallationHealth = (s?: string | null) => toneFrom(INSTALLATION_HEALTH_TONE, s);
export const toneForIntegrationDelivery = (s?: string | null) => toneFrom(INTEGRATION_DELIVERY_TONE, s);
export const toneForIntegrationJob = (s?: string | null) => toneFrom(INTEGRATION_JOB_TONE, s);
export const toneForWebhookEndpoint = (s?: string | null) => toneFrom(WEBHOOK_ENDPOINT_TONE, s);
export const toneForWebhookDelivery = (s?: string | null) => toneFrom(WEBHOOK_DELIVERY_TONE, s);
export const toneForApiClientStatus = (s?: string | null) => toneFrom(API_CLIENT_STATUS_TONE, s);

/** Deliveries that `POST .../replay` will actually accept — mirrors `IntegrationDeliveryService.replay`. */
export const INTEGRATION_REPLAYABLE_STATUSES = ['FAILED', 'DEAD_LETTERED', 'CANCELLED'];

/** An endpoint a replay can actually be sent to — `WebhooksService.replay` refuses every other status. */
export const WEBHOOK_REPLAY_REQUIRES_ACTIVE_ENDPOINT = true;
